import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import {MatButton, MatButtonModule} from '@angular/material/button';
import {Notification} from 'app/modules/admin/notifications/models/notification';
import {Subject, takeUntil} from 'rxjs';
import {Router, RouterLink} from '@angular/router';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatIconModule} from '@angular/material/icon';
import {DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet} from '@angular/common';
import {NotificationService} from 'app/modules/admin/notifications/services/notifications.service';
import {AuthService} from '@shared/services/auth.service';
import {ToDatePipe} from '@shared/pipes';
import {User} from '@users/models/user.interface';
import {fuseAnimations} from '@fuse/animations';
import {environment} from '@env/environment';
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {PushNotificationsService} from "@shared/cloudMessaging/services/push-notifications.service";

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    animations: fuseAnimations,
    imports: [MatButtonModule, NgIf, MatIconModule, MatTooltipModule, NgFor, NgClass, NgTemplateOutlet, RouterLink, DatePipe, ToDatePipe, MatProgressSpinnerModule]
})
export class NotificationsComponent implements OnInit, OnDestroy {

    readonly env = environment;
    readonly pushNotifications = inject(PushNotificationsService);

    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[] = [];

    customerID: string;
    authUser: User;

    unreadNotificationsCount: number = 0;
    isLoading = false;
    showDoubleTicks: boolean = false;
    markingAllAsRead: boolean;

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private router: Router,
        private notificationService: NotificationService,
        private authService: AuthService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.authUser = this.authService.authUser;
        //this.loadNotifications();
        this.listenToNotifications();
    }

    listenToNotifications(): void {

        this.isLoading = true;
        this.notificationService
            .collection((ref) => ref
                .orderBy('createdAt', 'desc')
                .where('read', '==', false)
            )
            .valueChanges({idField: 'id'})
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (unreadNotifications: Notification[]) => {

                    // Map notifications to wrap mentions in a span
                    this.notifications = unreadNotifications.map((notification) => {
                        notification.description = this.wrapMentionsInSpan(notification.description);
                        return notification;
                    });

                    // Get the length of unread notifications
                    this.unreadNotificationsCount = this.notifications.length;

                    this._changeDetectorRef.markForCheck(); // Trigger change detection
                    this.isLoading = false;
                },
                error: (error) => {
                    console.error('Error fetching notifications:', error);
                    this.isLoading = false;
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Enable FCM web push for this device (requires a user gesture)
     */
    async enablePush(): Promise<void> {
        await this.pushNotifications.enable();
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Disable FCM web push for this device
     */
    async disablePush(): Promise<void> {
        await this.pushNotifications.disable();
        this._changeDetectorRef.markForCheck();
    }

    async markAllAsRead(): Promise<void> {
        try {
            this.markingAllAsRead = true;
            this._changeDetectorRef.markForCheck();

            const batch = this.notificationService.batch();

            // Loop through each unread notification
            for (const notification of this.notifications) {


                if (!notification.read) {
                    // notification.readBy.push(this.authUser.uid); // Add the UID to the readBy array

                    const notificationToUpdate: Partial<Notification> = {
                        read: true
                    };

                    // Get a reference to the document in Firestore
                    const docRef = this.notificationService.collection().doc(notification.id).ref;

                    // Add the update to the batch
                    batch.update(docRef, notificationToUpdate);
                }
            }

            // Commit the batch to Firestore
            await batch.commit();

            // Reset unread notifications count
            this.unreadNotificationsCount = 0;
            this.markingAllAsRead = false;
            this._changeDetectorRef.markForCheck();

            console.log("notifications marked as read successfully !!");

        } catch (error) {
            console.error('Error marking notifications as read:', error);
            this.markingAllAsRead = false;
            this._changeDetectorRef.markForCheck();
        }
    }


    async markAsRead(notification: Notification): Promise<void> {
        try {
            const batch = this.notificationService.batch();

            if (!notification.read) {
                // notification.readBy.push(this.authUser.uid); // Add the UID to the readBy array

                // Add the UID to the readBy array
                const notificationToUpdate: Partial<Notification> = {
                    read: true
                };

                // Get a reference to the document in Firestore
                const docRef = this.notificationService.collection().doc(notification.id).ref; // Use .ref to get the DocumentReference

                // Add the update to the batch
                batch.update(docRef, notificationToUpdate);

                // Commit the batch to Firestore
                await batch.commit();


                this.unreadNotificationsCount--;
                this._changeDetectorRef.markForCheck();
            }
        } catch (error) {
            console.error('Error marking the notification as read:', error);
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item?.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    goToNotifications() {
        this.closePanel();
        this.router.navigate(['notifications/alle'])
    }

    navigateToNotificationSource(notification: Notification) {
        this.markAsRead(notification);
        this.closePanel();
        this.router.navigate([notification.link]);
    }

    private wrapMentionsInSpan(content: string): string {
        if (!content) {
            return '';
        }

        // Regular expression to find mentions
        const mentionRegex = /(@[a-zA-Z0-9_]+)/g;

        // Replace mentions with span-wrapped mentions
        const transformedContent = content.replace(mentionRegex, (mention) => {
            return `<span class="mention">${mention}</span>`;
        });

        return `${transformedContent}`;
    }
}
